import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { utcHour } from "@/lib/studio";

const FADE_MS = 8000;

const chime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [660, 880, 1320].forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain(), t = ctx.currentTime + i * 0.18;
      o.frequency.value = f; o.type = "sine";
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.25, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
      o.connect(g).connect(ctx.destination); o.start(t); o.stop(t + 1.3);
    });
    setTimeout(() => ctx.close(), 2500);
  } catch { /* audio unavailable */ }
};

const wordsSince = async (startHour) => {
  const { data } = await api.get("/writing/stats", { params: { start: startHour, end: utcHour(new Date()) } });
  return data.hours.reduce((a, h) => a + h.words, 0);
};

export const useSprint = ({ audio, volume, history, setHistory }) => {
  const [sprint, setSprint] = useState(null);
  const [result, setResult] = useState(null);
  const fading = useRef(null);

  const fadeOut = useCallback(() => {
    const a = audio.current;
    if (!a || a.paused) return;
    const v0 = a.volume, t0 = Date.now();
    clearInterval(fading.current);
    fading.current = setInterval(() => {
      const k = Math.min(1, (Date.now() - t0) / FADE_MS);
      a.volume = v0 * (1 - k);
      if (k >= 1) { clearInterval(fading.current); a.pause(); a.volume = volume; }
    }, 100);
  }, [audio, volume]);

  const start = async (minutes, mood) => {
    const startHour = utcHour(new Date());
    let baseline = 0;
    try { baseline = await wordsSince(startHour); } catch { /* counted as 0 */ }
    setResult(null);
    setSprint({ minutes, mood, startHour, baseline, endsAt: Date.now() + minutes * 60000 });
  };

  const finish = useCallback(async (s, early) => {
    setSprint(null);
    fadeOut();
    chime();
    setResult({ minutes: s.minutes, words: null });
    await new Promise((r) => setTimeout(r, 4000));
    let words = null;
    try { words = Math.max(0, (await wordsSince(s.startHour)) - s.baseline); } catch { /* unavailable offline */ }
    const mins = early ? Math.max(1, Math.round((Date.now() - (s.endsAt - s.minutes * 60000)) / 60000)) : s.minutes;
    const r = { at: new Date().toISOString(), minutes: mins, words, mood: s.mood || null };
    setResult(r);
    setHistory((h) => [r, ...(h || [])].slice(0, 20));
    toast.success(words == null ? `${mins}-minute sprint done!` : `${mins}-minute sprint: ${words.toLocaleString()} words`, { duration: 8000 });
  }, [fadeOut, setHistory]);

  useEffect(() => {
    if (!sprint) return undefined;
    const t = setInterval(() => { if (Date.now() >= sprint.endsAt) finish(sprint, false); }, 500);
    return () => clearInterval(t);
  }, [sprint, finish]);

  useEffect(() => () => clearInterval(fading.current), []);
  return { sprint, result, history, startSprint: start, stopSprint: () => sprint && finish(sprint, true), clearResult: () => setResult(null) };
};
